// S3 · the import upload lane (Module 02 §4 S3, §3.3 step 1).
//
// Three things in order: get the template, choose what happens to rows that
// already exist, drop the file. Then the round trip the contract describes —
// `POST /v1/imports` returns the job plus a 15-minute presigned PUT, the browser
// uploads the bytes straight to S3, and the screen hands off to S4, which starts
// the parse.
//
// The presigned PUT is the ONE request in this area that does not go through the
// generated api-client, and it cannot: it is an S3 object upload to a URL the
// API just minted for us (§3.3 "Client uploads, then POST …/parse"). Every
// decision around it — that the job exists, where the bytes may go, for how long
// — was made by the API.
//
// The client-side size/extension check is a courtesy, not a gate: the parser
// re-checks the byte cap authoritatively against the uploaded object (§9.15).

import { useRef, useState, useSyncExternalStore } from "react";
import { onlineManager } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { useAttributeSchemas } from "../../../office/catalogue/hooks/catalogue-queries";
import { useCreateImport } from "../../hooks/onboarding-queries";
import {
  ACCEPTED_EXTENSIONS,
  DUPLICATE_POLICIES,
  MAX_FILE_MB,
  attributeKeysOf,
  buildTemplateCsv,
  formatBytes,
  rejectFile,
  templateFileName,
  type DuplicatePolicy,
  type FileRejection,
  type ImportKind,
} from "./import-model";
import { ImportProblemNotice } from "./import-problem-notice";
import { useImportLabels } from "./labels";

export interface UploadPanelProps {
  /** The lanes this step offers. */
  kinds: readonly ImportKind[];
  /** §7 lane gate — the mirror; the server is the boundary. */
  canImport: (kind: ImportKind) => boolean;
  /** Where a finished upload goes. The wizard's S4 page by default; a page
   * that hosts the flow elsewhere (spec 09 §4.18's Import customers, P3-E2 PR
   * E2-2c) opens the job in its own area. */
  onUploaded?: (jobId: string) => void;
}

type Phase = "idle" | "creating" | "uploading";

/** The browser's connectivity as react-query tracks it (spec 02 §4 S3, spec 09
 * §4.18): an upload is a `POST /v1/imports` and a presigned PUT, neither of
 * which can be queued for later, so Upload is disabled while offline and one
 * line says why. Read from react-query's `onlineManager`, the flag every
 * office read already pauses on, so the panel and the page's reads agree. */
function subscribeOnline(onChange: () => void): () => void {
  return onlineManager.subscribe(onChange);
}

function useBrowserOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => onlineManager.isOnline(),
    () => true,
  );
}

/** How long `POST /v1/imports` says the presigned PUT stays valid (§3.3 step 1). */
const PRESIGN_TTL_MS = 15 * 60 * 1000;

/** A created-but-not-yet-uploaded job, kept so a retry reuses it. */
interface PendingJob {
  readonly id: string;
  readonly uploadUrl: string;
  readonly mintedAt: number;
  readonly fileName: string;
  readonly kind: ImportKind;
}

export function ImportUploadPanel({ kinds, canImport, onUploaded }: UploadPanelProps) {
  const { t } = useTranslation();
  const labels = useImportLabels();
  const navigate = useNavigate();
  const create = useCreateImport();
  const schemas = useAttributeSchemas();
  const fileInput = useRef<HTMLInputElement>(null);

  const [kind, setKind] = useState<ImportKind>(kinds[0] ?? "products");
  const [policy, setPolicy] = useState<DuplicatePolicy>("reject");
  const [file, setFile] = useState<File | null>(null);
  const [rejection, setRejection] = useState<(FileRejection & { size: number }) | undefined>(
    undefined,
  );
  const [phase, setPhase] = useState<Phase>("idle");
  const [uploadFailed, setUploadFailed] = useState(false);
  const [pendingJob, setPendingJob] = useState<PendingJob | null>(null);
  const [sentBytes, setSentBytes] = useState(0);
  const uploadXhr = useRef<XMLHttpRequest | null>(null);

  const permitted = canImport(kind);
  const online = useBrowserOnline();
  const busy = phase !== "idle";
  const percent = file !== null && file.size > 0 ? Math.round((sentBytes / file.size) * 100) : 0;

  function chooseFile(next: File | null) {
    setUploadFailed(false);
    setSentBytes(0);
    // A different file needs a different job: the presign is minted for the
    // name + size the API was told about.
    setPendingJob(null);
    if (next === null) {
      setFile(null);
      setRejection(undefined);
      return;
    }
    const problem = rejectFile(next);
    setRejection(problem === undefined ? undefined : { ...problem, size: next.size });
    setFile(problem === undefined ? next : null);
  }

  function downloadTemplate() {
    // The pack's .xlsx template binaries are still deferred (D-032), so the
    // template is generated from the same field catalog the importer maps
    // against plus the attribute columns the installed pack actually seeded —
    // a file that genuinely round-trips today rather than a dead download link.
    const attributeKeys = (schemas.data?.items ?? [])
      .filter((s) => s.status === "active")
      .flatMap((s) => attributeKeysOf(s.json_schema));
    const csv = buildTemplateCsv(kind, [...new Set(attributeKeys)]);
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = templateFileName(kind);
    // The anchor MUST be in the document (a detached `click()` is ignored in
    // some browsers) and the object URL must outlive the click (revoking it
    // synchronously can cancel the download before it starts). This is the only
    // way a user gets a template today, so a silent no-op here stops onboarding
    // at minute one.
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }

  /** Upload `file` to an already-minted presigned URL, reporting progress.
   * XHR, not fetch: `fetch` cannot report upload progress, and a 100 MB
   * workbook on a factory link otherwise shows a static "Uploading…" that
   * reads as a hang. */
  function putToS3(url: string, body: File): Promise<"ok" | "failed" | "aborted"> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      uploadXhr.current = xhr;
      xhr.open("PUT", url);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) setSentBytes(event.loaded);
      };
      xhr.onload = () => {
        uploadXhr.current = null;
        resolve(xhr.status >= 200 && xhr.status < 300 ? "ok" : "failed");
      };
      xhr.onerror = () => {
        uploadXhr.current = null;
        resolve("failed");
      };
      xhr.onabort = () => {
        uploadXhr.current = null;
        resolve("aborted");
      };
      xhr.send(body);
    });
  }

  /**
   * Create the job (once) and upload the bytes.
   *
   * The created job + its presigned URL are HELD in state, so a retry after a
   * flaky S3 PUT re-uploads to the SAME url instead of minting a second job.
   * The old behaviour left one un-parseable job stranded in `uploaded` per
   * retry, and nothing garbage-collects those. A presign is good for 15 minutes
   * (§3.3 step 1); past that the job is re-created, and the copy says so.
   */
  async function startUpload() {
    if (file === null || !permitted) return;
    setUploadFailed(false);
    setSentBytes(0);

    let job = pendingJob;
    const presignFresh = job !== null && Date.now() - job.mintedAt < PRESIGN_TTL_MS;
    if (job === null || !presignFresh || job.fileName !== file.name || job.kind !== kind) {
      setPhase("creating");
      try {
        const created = await create.mutateAsync({
          kind,
          file_name: file.name,
          file_size_bytes: file.size,
          duplicate_policy: policy,
        });
        job = {
          id: created.job.id,
          uploadUrl: created.upload_url,
          mintedAt: Date.now(),
          fileName: file.name,
          kind,
        };
        setPendingJob(job);
      } catch {
        // Rendered by the problem notice below.
        setPhase("idle");
        return;
      }
    }

    setPhase("uploading");
    const result = await putToS3(job.uploadUrl, file);
    setPhase("idle");
    if (result === "aborted") return;
    if (result === "failed") {
      setUploadFailed(true);
      return;
    }
    setFile(null);
    setPendingJob(null);
    setSentBytes(0);
    if (onUploaded !== undefined) {
      onUploaded(job.id);
      return;
    }
    await navigate({ to: "/onboarding/imports/$id", params: { id: job.id } });
  }

  function cancelUpload() {
    uploadXhr.current?.abort();
  }

  return (
    <section className="import-upload ds-card" data-testid="import-upload">
      <h3>{t("onboarding.catalogueStep.importTab.title")}</h3>
      <p className="wizard-step-blurb">{t("onboarding.catalogueStep.importTab.blurb")}</p>

      <Field label={t("onboarding.catalogueStep.importTab.kindLabel")}>
        <Select
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as ImportKind);
            chooseFile(null);
          }}
        >
          {kinds.map((k) => (
            <option key={k} value={k}>
              {labels.kindLabel(k)}
            </option>
          ))}
        </Select>
      </Field>

      {!permitted ? (
        <div className="state state-empty wizard-notice" role="status" data-testid="lane-forbidden">
          <p className="state-title">{t("onboarding.catalogueStep.importTab.notPermitted")}</p>
          <p className="state-body">{t("onboarding.catalogueStep.importTab.notPermittedBody")}</p>
        </div>
      ) : null}

      <div className="import-template">
        <h4>{t("onboarding.catalogueStep.importTab.templateTitle")}</h4>
        <p className="wizard-step-blurb">{t("onboarding.catalogueStep.importTab.templateHint")}</p>
        <Button onClick={downloadTemplate}>
          {t("onboarding.catalogueStep.importTab.download")}
        </Button>
      </div>

      <div className="import-dropzone-block">
        <h4>{t("onboarding.catalogueStep.importTab.uploadTitle")}</h4>

        <Field label={t("onboarding.importShared.duplicatePolicy.label")}>
          <Select
            value={policy}
            disabled={!permitted || busy}
            onChange={(e) => {
              setPolicy(e.target.value as DuplicatePolicy);
            }}
          >
            {DUPLICATE_POLICIES.map((p) => (
              <option key={p} value={p}>
                {labels.policyLabel(p)}
              </option>
            ))}
          </Select>
          <small className="filter-help">
            {t(`onboarding.importShared.duplicatePolicy.${policy}Help`)}
          </small>
          <small className="filter-help">{t("onboarding.importShared.duplicatePolicy.help")}</small>
        </Field>

        <div
          className="import-dropzone"
          data-testid="import-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (!permitted || busy || !online) return;
            chooseFile(e.dataTransfer.files.item(0));
          }}
        >
          <p className="import-dropzone-label">{t("onboarding.catalogueStep.dropzone.label")}</p>
          <p className="import-dropzone-hint">
            {t("onboarding.catalogueStep.dropzone.hint", { sizeMb: MAX_FILE_MB })}
          </p>
          {/* The BROWSE control is the label, styled on the register; the native
              input is visually hidden inside it. It used to be the other way
              round, so the wizard's own step rendered a raw OS "Choose File | No
              file chosen" widget beside design-system buttons (surface review
              F11). Wrapping the input keeps the click target and the accessible
              name without a JS click-forwarder. */}
          <label className="import-dropzone-browse">
            <span>{t("onboarding.catalogueStep.dropzone.browse")}</span>
            <input
              ref={fileInput}
              className="visually-hidden"
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(",")}
              disabled={!permitted || busy || !online}
              onChange={(e) => {
                chooseFile(e.target.files?.item(0) ?? null);
              }}
            />
          </label>
        </div>

        {rejection !== undefined ? (
          <p className="state state-error import-file-problem" role="alert">
            {rejection.reason === "too_large"
              ? t("onboarding.catalogueStep.fileProblem.tooLarge", {
                  size: formatBytes(rejection.size),
                  sizeMb: MAX_FILE_MB,
                })
              : t("onboarding.catalogueStep.fileProblem.unsupported", {
                  name: t("onboarding.catalogueStep.dropzone.accept"),
                })}
          </p>
        ) : null}

        {file !== null ? (
          <p className="import-file-chosen" data-testid="import-file-chosen">
            {t("onboarding.catalogueStep.dropzone.chosen", {
              name: file.name,
              size: formatBytes(file.size),
            })}
            <button
              type="button"
              className="ds-lnk"
              onClick={() => {
                chooseFile(null);
                if (fileInput.current !== null) fileInput.current.value = "";
              }}
            >
              {t("onboarding.catalogueStep.dropzone.clear")}
            </button>
          </p>
        ) : null}

        {create.error !== null ? (
          <ImportProblemNotice
            error={create.error}
            onRetry={() => {
              create.reset();
            }}
          />
        ) : null}

        {phase === "uploading" && file !== null ? (
          <div className="import-upload-progress" data-testid="upload-progress">
            <progress
              max={file.size}
              value={sentBytes}
              aria-labelledby="import-upload-progress-text"
            />
            <p id="import-upload-progress-text" className="wizard-save-bar-note" role="status">
              {t("onboarding.catalogueStep.upload.progress", {
                percent,
                sent: formatBytes(sentBytes),
                total: formatBytes(file.size),
              })}
            </p>
            <Button onClick={cancelUpload}>{t("onboarding.catalogueStep.upload.cancel")}</Button>
          </div>
        ) : null}

        {uploadFailed ? (
          <div className="state state-error wizard-notice" role="alert" data-testid="upload-failed">
            <p className="state-title">{t("onboarding.catalogueStep.upload.failedTitle")}</p>
            <p className="state-detail">{t("onboarding.catalogueStep.upload.failedBody")}</p>
            {/* Retries re-PUT to the SAME presigned url while it is still valid,
                so a flaky link no longer leaves one stranded job per attempt. */}
            <p className="wizard-save-bar-note">
              {t("onboarding.catalogueStep.upload.retryReusesJob")}
            </p>
            <Button onClick={() => void startUpload()}>
              {t("onboarding.catalogueStep.upload.retry")}
            </Button>
          </div>
        ) : null}

        {!online ? (
          <p className="wizard-save-bar-note" role="status" data-testid="upload-offline">
            {t("onboarding.catalogueStep.upload.offline")}
          </p>
        ) : null}

        <Button
          variant="primary"
          data-testid="import-upload-start"
          disabled={file === null || !permitted || busy || !online}
          onClick={() => void startUpload()}
        >
          {phase === "creating"
            ? t("onboarding.catalogueStep.upload.creating")
            : phase === "uploading"
              ? t("onboarding.catalogueStep.upload.uploading")
              : t("onboarding.catalogueStep.upload.start")}
        </Button>
      </div>
    </section>
  );
}
